import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Trash2, X } from 'lucide-react';
import { UserDeletionDependencies, UserProfile } from '../../types';
import { storage } from '../../services/storage';
import { getStripePrices } from '../../services/stripeService';
import { useModal } from '../ui/ModalProvider';

interface DeleteUserModalProps {
  isOpen: boolean;
  user: UserProfile | null;
  onClose: () => void;
  onDeleted: () => void;
}

const formatUserLabel = (profile: UserProfile) => {
  const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim();
  return name ? `${name} (${profile.email})` : profile.email;
};

export const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  isOpen,
  user,
  onClose,
  onDeleted,
}) => {
  const { toast } = useModal();
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [dependencies, setDependencies] = useState<UserDeletionDependencies | null>(null);
  const [targets, setTargets] = useState<UserProfile[]>([]);
  const [reassignToUserId, setReassignToUserId] = useState('');
  const [priceInfo, setPriceInfo] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const blockingItems = useMemo(
    () => dependencies?.blocking?.filter((item) => item.count > 0) ?? [],
    [dependencies]
  );

  const informationalItems = useMemo(
    () => dependencies?.informational?.filter((item) => item.count > 0) ?? [],
    [dependencies]
  );

  const requiresReassign = blockingItems.length > 0;
  const canSubmit = !requiresReassign || !!reassignToUserId;

  useEffect(() => {
    if (!isOpen || !user) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      setDependencies(null);
      setTargets([]);
      setReassignToUserId('');
      setPriceInfo('');

      try {
        const [{ data: deps, error: depsError }, eligibleTargets] = await Promise.all([
          storage.getUserDeletionDependencies(user.id),
          storage.getEligibleReassignmentTargets(user.id),
        ]);

        if (cancelled) return;

        if (depsError) {
          setLoadError(depsError.message || 'Impossible de charger les dépendances');
          return;
        }

        setDependencies(deps);
        setTargets(eligibleTargets);

        if (user.role !== 'prestataire_marketing' && user.accessLevel) {
          try {
            const { prices } = await getStripePrices();
            const price = user.accessLevel === 'national' ? prices.national : prices.local;
            if (price && !price.error) {
              setPriceInfo(`L'abonnement sera mis à jour : -${price.amount} ${price.currency}/mois`);
            }
          } catch {
            // ignore stripe price errors
          }
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Erreur inconnue';
          setLoadError(message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const handleDelete = async () => {
    if (!canSubmit) return;

    setDeleting(true);
    try {
      const { error } = await storage.deleteUser(
        user.id,
        requiresReassign ? reassignToUserId : undefined
      );

      if (error) {
        toast.error(error.message || 'Erreur lors de la suppression');
        return;
      }

      onDeleted();
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"><div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 animate-in zoom-in-95"><div className="flex justify-between items-start mb-4"><h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-500" />Supprimer l'utilisateur ?</h3><button type="button" onClick={onClose} className="hover:bg-slate-200 rounded-full p-1 transition-colors"><X className="w-5 h-5 text-slate-400" /></button></div>
        {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Analyse des dépendances...</div>
        ) : loadError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800">{loadError}</div>
        ) : (
        <div className="space-y-4"><p className="text-sm text-slate-600">Vous allez supprimer <strong>{formatUserLabel(user)}</strong>. Cette action est irréversible.</p>{priceInfo && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">{priceInfo}</p>}{blockingItems.length > 0 && (<div className="rounded-lg border border-amber-200 bg-amber-50 p-4"><div className="flex items-center gap-2 text-amber-900 font-semibold text-sm mb-2"><AlertTriangle className="w-4 h-4" /> Réaffectation obligatoire</div><ul className="text-sm text-amber-900 space-y-1 list-disc list-inside">{blockingItems.map((item) => (<li key={item.key}>{item.label} : {item.count}</li>))}</ul></div>)}{informationalItems.length > 0 && (<div className="rounded-lg border border-slate-200 bg-slate-50 p-4"><p className="text-xs font-semibold text-slate-500 uppercase mb-2">Historique (conservé, lien utilisateur retiré)</p><ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">{informationalItems.map((item) => (<li key={item.key}>{item.label} : {item.count}</li>))}</ul></div>)}{requiresReassign && (<div className="space-y-2"><label className="block text-sm font-bold text-slate-700">Réaffecter à *</label><select  value={reassignToUserId}  onChange={(e) => setReassignToUserId(e.target.value)}  className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none"><option value="">Choisir un utilisateur</option>{targets.map((target) => (<option key={target.id} value={target.id}>{formatUserLabel(target)}</option>))}</select>{targets.length === 0 && (
                <p className="text-xs text-red-600">
                  Aucun utilisateur éligible. Créez ou assignez un autre{' '}
                  {user.role === 'prestataire_marketing'
                    ? 'collègue sur la même société prestataire, un administrateur ou un superadmin'
                    : 'utilisateur admin, national, ou partageant la même agence'}
                  , puis réessayez.
                </p>
              )}</div>)}</div>
        )}
        <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-slate-200"><button type="button" onClick={onClose} disabled={deleting} className="px-6 py-2.5 text-slate-500 hover:bg-slate-100 rounded-xl font-bold transition-colors disabled:opacity-50">Annuler</button><button  type="button"  onClick={handleDelete}  disabled={deleting || !canSubmit || (requiresReassign && targets.length === 0)}  className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">{deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}Supprimer définitivement</button></div></div></div>
  );
};
